import { SalesInfoPageOption } from '@/types/enums';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// 필터 타입 정의
export interface FilterState {
  // 조직 계층 (회사는 별도 store로 분리)
  organization: string[];
  business: string[];
  store: string[];

  // 날짜 관련
  dateRange: {
    start: string;
    end: string;
  };
  dateType: 'CLOSEDT' | 'SDATE'; // 마감일 | 승인일

  // 카드매출 옵션
  cardSalesOptions: string[];
  cardSalesDateType: 'CLOSEDT' | 'SDATE';

  // OCT 옵션
  octDateType: 'noDate' | 'cdate' | 'udate';
  octCStatus: string;
  octBOption: string;

  // 세차상세 삭제자료조회
  carWashRefund: boolean;

  // 페이지 옵션
  pageOptions: string[];
  salesinfoPageOptions: (keyof typeof SalesInfoPageOption)[];

  // 재고조정
  purchaseAdjustment: boolean;

  // 금액 범위
  amountRange: {
    min: string;
    max: string;
  };

  // 마감 관련
  closeDate: string;
  closeCount: string;

  // 검색 필드들
  carNumber: string;
  cardNumber: string;
  approvalNumber: string;
  issueCode: string;

  // 승인 타입
  approvalType: 'ALL' | 'CONFIRM' | 'CANCEL';

  // 거래 타입, 거래처, 카드거래처
  dealType: string[];
  dealer: string[];
  cardDealer: string[];

  // 주유 관련
  oil: string[];
  tank: string[];
  self: string[];
  nozzle: string[];
  cat: string;

  isExcludeTankLorry: boolean;

  // 세차 관련
  carWashIsIncludeRefund: boolean;
}

// 필터 액션 타입
interface FilterActions {
  updateFilter: <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => void;
  updateDateRange: (start: string, end: string) => void;
  updateAmountRange: (min: string, max: string) => void;
  resetFilters: () => void;
  getFiltersByPage: (pageId: string) => Partial<FilterState>;
  setPageFilters: (pageId: string, filters: Partial<FilterState>) => void;

  // API 파라미터 빌드 관련 액션
  buildApiParams: (
    pageId: string,
    storeCodeList: string[],
    additionalParams?: Record<string, string | number>
  ) => URLSearchParams;
  buildApiParamsWithExtensions: (
    pageId: string,
    storeCodeList: string[],
    additionalParams?: Record<string, string | number>,
    customOptions?: Record<string, any>
  ) => URLSearchParams;
}

// 초기 상태
const initialState: FilterState = {
  organization: [],
  business: [],
  store: [],
  dateRange: {
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  },
  dateType: 'CLOSEDT',
  cardSalesOptions: [],
  cardSalesDateType: 'CLOSEDT',
  octDateType: 'noDate',
  octCStatus: 'ALL',
  octBOption: 'ALL',
  carWashRefund: false,
  pageOptions: [],
  salesinfoPageOptions: [],
  purchaseAdjustment: false,
  amountRange: {
    min: '',
    max: '',
  },
  closeDate: '',
  closeCount: '',
  carNumber: '',
  cardNumber: '',
  approvalNumber: '',
  issueCode: '',
  approvalType: 'ALL',
  dealType: [],
  dealer: [],
  cardDealer: [],
  oil: [],
  tank: [],
  self: [],
  nozzle: [],
  cat: '',
  isExcludeTankLorry: false,
  carWashIsIncludeRefund: false,
};

// Zustand store 생성
export const useFilterStore = create<FilterState & FilterActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      updateFilter: (key, value) =>
        set(state => {
          const prevValue = state[key];
          if (areFilterValuesEqual(prevValue, value)) {
            return state;
          }
          return {
            ...state,
            [key]: value,
          };
        }),

      updateDateRange: (start, end) =>
        set(state => {
          const next = { start, end } as FilterState['dateRange'];
          if (areFilterValuesEqual(state.dateRange, next)) {
            return state;
          }
          return {
            ...state,
            dateRange: next,
          };
        }),

      updateAmountRange: (min, max) =>
        set(state => {
          const next = { min, max } as FilterState['amountRange'];
          if (areFilterValuesEqual(state.amountRange, next)) {
            return state;
          }
          return {
            ...state,
            amountRange: next,
          };
        }),

      resetFilters: () => set(initialState),

      getFiltersByPage: () => {
        const state = get();
        // 페이지별로 필요한 필터만 반환하는 로직
        // 현재는 모든 필터를 반환하지만, 나중에 페이지별 설정에 따라 필터링
        return state;
      },

      setPageFilters: (_pageId: string, filters: Partial<FilterState>) =>
        set(state => ({
          ...state,
          ...filters,
        })),

      // API 파라미터 빌드 메서드
      buildApiParams: (
        pageId: string,
        storeCodeList: string[],
        additionalParams = {}
      ) => {
        const state = get();
        const params = new URLSearchParams();
        const pageConfig = getPageFilterConfig(pageId);

        // Store 코드는 항상 추가 (API마다 파라미터명이 다를 수 있음)
        storeCodeList.forEach(storeCode => {
          params.append('storeCd', storeCode);
          params.append('storeCodeList', storeCode); // 일부 API는 이 이름을 사용
        });

        // 페이지에서 필요한 필터만 처리
        const relevantFilters = [
          ...pageConfig.required,
          ...pageConfig.optional,
        ];

        relevantFilters.forEach(filterKey => {
          // store 는 위에서 storeCodeList 로 이미 넣었다 - 여기서 또 넣으면 storeCd 가 중복된다
          if (filterKey === 'store') {
            return;
          }

          const filterValue = state[filterKey];
          const mapping = FILTER_PARAM_MAPPING[filterKey];

          // 값이 없거나 빈 배열이면 건너뛰기
          if (
            filterValue === undefined ||
            filterValue === null ||
            filterValue === ''
          ) {
            return;
          }

          if (Array.isArray(filterValue) && filterValue.length === 0) {
            return;
          }

          // 매핑이 함수인 경우 (복잡한 처리)
          if (typeof mapping === 'function') {
            mapping(filterValue, params);
            return;
          }

          // 매핑이 문자열인 경우
          if (typeof mapping === 'string') {
            if (Array.isArray(filterValue)) {
              // 배열 타입 - 각 값을 개별적으로 append
              filterValue.forEach(value => {
                if (value !== null && value !== undefined && value !== '') {
                  params.append(mapping, value.toString());
                }
              });
            } else if (typeof filterValue === 'boolean') {
              // 불린 타입 - true일 때만 추가하거나 문자열로 변환
              if (filterValue) {
                params.append(mapping, 'true');
              }
            } else if (
              typeof filterValue === 'object' &&
              filterValue !== null
            ) {
              // 객체 타입은 특별 처리 함수에서 처리되어야 함
              console.warn(
                `Object type filter ${filterKey} should use function mapping`
              );
            } else {
              // 문자열, 숫자 등
              params.append(mapping, filterValue.toString());
            }
          }
        });

        // 추가 파라미터 처리 (페이지네이션 등)
        Object.entries(additionalParams).forEach(([key, value]) => {
          params.append(key, value.toString());
        });

        return params;
      },

      // 확장된 API 파라미터 빌드 메서드 (페이지별 특수 처리 포함)
      buildApiParamsWithExtensions: (
        pageId: string,
        storeCodeList: string[],
        additionalParams = {},
        customOptions = {}
      ) => {
        const state = get();
        const baseParams = state.buildApiParams(
          pageId,
          storeCodeList,
          additionalParams
        );

        // 페이지별 특수 처리 적용
        applyPageSpecificParams(baseParams, pageId, state, customOptions);

        return baseParams;
      },
    }),
    {
      name: 'wjpnc-filter-storage',
      partialize: state => {
        // 지속성에서 제외할 필드들 (함수 등)
        const {
          organization,
          business,
          store,
          dateRange,
          dateType,
          cardSalesOptions,
          cardSalesDateType,
          octDateType,
          octCStatus,
          octBOption,
          carWashRefund,
          pageOptions,
          salesinfoPageOptions,
          purchaseAdjustment,
          amountRange,
          closeDate,
          closeCount,
          carNumber,
          cardNumber,
          approvalNumber,
          issueCode,
          approvalType,
          dealType,
          dealer,
          cardDealer,
          oil,
          tank,
          self,
          nozzle,
          cat,
          isExcludeTankLorry,
          carWashIsIncludeRefund,
        } = state;

        return {
          organization,
          business,
          store,
          dateRange,
          dateType,
          cardSalesOptions,
          cardSalesDateType,
          octDateType,
          octCStatus,
          octBOption,
          carWashRefund,
          pageOptions,
          salesinfoPageOptions,
          purchaseAdjustment,
          amountRange,
          closeDate,
          closeCount,
          carNumber,
          cardNumber,
          approvalNumber,
          issueCode,
          approvalType,
          dealType,
          dealer,
          cardDealer,
          oil,
          tank,
          self,
          nozzle,
          cat,
          isExcludeTankLorry,
          carWashIsIncludeRefund,
        };
      },
    }
  )
);

// 페이지별 필터 설정 타입
export interface PageFilterConfig {
  required: (keyof FilterState)[];
  optional: (keyof FilterState)[];
  disabled?: boolean; // 필터바 자체를 비활성화
  singleStore?: boolean; // 단일 주유소만 선택 가능
  pageSpecific?: {
    [key: string]: any;
  };
}

// 페이지별 필터 설정
export const PAGE_FILTER_CONFIGS: Record<string, PageFilterConfig> = {
  // 대시보드 - 필터 사용 안함
  dashboard: {
    required: [],
    optional: [],
    disabled: true,
  },

  // 매출현황
  salesinfo: {
    required: ['organization', 'store', 'dateRange'],
    optional: [
      'salesinfoPageOptions',
      'dealType',
      'dealer',
      'oil',
      'self',
      'nozzle',
      'tank',
      'carNumber',
      'cardNumber',
      'approvalNumber',
      'amountRange',
    ],
  },

  // 신용카드 매출현황
  cardsales: {
    required: ['organization', 'store', 'dateRange'],
    optional: [
      'dateType',
      'cardSalesOptions',
      'cardNumber',
      'approvalNumber',
      'amountRange',
      'approvalType',
      'cat',
      'cardDealer',
      'dealer',
    ],
  },

  // 셀프 판매현황
  selfsales: {
    required: ['organization', 'store', 'dateRange'],
    optional: ['dateType', 'pageOptions', 'oil', 'tank', 'self', 'nozzle'],
  },

  // 탱크재고
  tankjg: {
    required: ['organization', 'store', 'dateRange'],
    optional: ['pageOptions', 'tank', 'oil', 'isExcludeTankLorry'],
  },

  // TLG 실시간
  'tlg-realtime': {
    required: ['organization', 'store'],
    optional: [],
  },

  // 마감현황
  'close-stat': {
    required: ['organization', 'store', 'closeDate', 'closeCount'],
    optional: [],
    singleStore: true, // 단일 주유소만 선택 가능
  },

  // 매입자료
  purchase: {
    required: ['organization', 'store', 'dateRange'],
    optional: ['purchaseAdjustment', 'dealType', 'dealer'],
  },

  // OCT
  oct: {
    required: ['organization', 'store'],
    optional: [
      'octDateType',
      'octCStatus',
      'octBOption',
      'issueCode',
      'dateRange',
      'oil',
      'carNumber',
      'dealer',
    ],
  },

  // 종합일보
  aggregate: {
    required: ['organization', 'store', 'dateRange'],
    optional: [],
  },

  // 배송현황
  delivery: {
    required: ['organization', 'store', 'dateRange'],
    optional: [],
  },

  // 판매자료 집계
  timesale: {
    required: ['organization', 'store', 'dateRange'],
    optional: [],
  },

  // 입출금자료
  money: {
    required: ['organization', 'store', 'dateRange'],
    optional: [],
  },

  // 세차현황
  'car-wash': {
    required: ['organization', 'store', 'dateRange'],
    optional: ['carWashIsIncludeRefund'],
  },

  // 거래명세서 (발행년월은 화면 로컬 state로 관리)
  //
  // optional 을 비워 상세 필터 버튼을 감춘다. 거래처는 화면 안의 임의발행 Select 로 고르고
  // 이 화면은 전역 dealer 를 읽지 않아, 필터바에서 고르면 아무 일도 일어나지 않았다.
  invoice: {
    required: ['organization', 'store'],
    optional: [],
    singleStore: true, // 발행 API 가 발행부서 1곳만 받는다
  },

  // 세금계산서 (발행기간은 화면 로컬 state로 관리 - 거래명세서와 동일)
  //
  // 전역 dateRange 를 두지 않는다. 이 화면의 발행기간은 "시작월=종료월" 같은 고유 규칙을 갖고
  // 화면 자체 입력만 조회에 쓰이는데, 전역 기간까지 떠 있으면 어느 쪽이 적용되는지 알 수 없다.
  // 상세 필터를 감추는 이유는 거래명세서와 같다.
  'tax-invoice': {
    required: ['organization', 'store'],
    optional: [],
    singleStore: true, // 발행 API 가 발행부서 1곳만 받는다
  },

  // 매출 총계
  salestotal: {
    required: ['organization', 'store', 'dateRange'],
    optional: ['dealType', 'dealer', 'oil', 'nozzle', 'tank', 'carNumber'],
  },

  // 필터가 필요 없는 페이지들
  'user-management': {
    required: [],
    optional: [],
    disabled: true,
  },

  'organization-management': {
    required: [],
    optional: [],
    disabled: true,
  },

  'station-management': {
    required: [],
    optional: [],
    disabled: true,
  },

  'user-approval': {
    required: [],
    optional: [],
    disabled: true,
  },

  'menu-role-sets': {
    required: [],
    optional: [],
    disabled: true,
  },

  'data-transfer-status': {
    required: [],
    optional: [],
    disabled: true,
  },

  account: {
    required: [],
    optional: [],
    disabled: true,
  },

  cust: {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  'cust-card': {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  'cust-cost': {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  car: {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  tank: {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  oil: {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  nozzle: {
    required: ['organization'],
    // business/store 는 상세 필터 패널에 렌더 블록이 없고 기본 바의 조직 섹션이 처리한다.
    // optional 에 남겨 두면 내용 없는 상세 필터 버튼만 열린다.
    optional: [],
  },

  // 기본 설정 (모든 필터 사용 가능)
  default: {
    required: ['organization', 'store'],
    optional: Object.keys(initialState) as (keyof FilterState)[],
  },
};

// 페이지별 필터 가져오기 헬퍼 함수
export const getPageFilterConfig = (pageId: string): PageFilterConfig => {
  return PAGE_FILTER_CONFIGS[pageId] || PAGE_FILTER_CONFIGS.default;
};

// 얕은/간단 비교: 배열은 길이와 각 요소 비교, 객체는 각 필드 비교, 그 외는 엄격 동등
function areFilterValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index++) {
      if (a[index] !== b[index]) return false;
    }
    return true;
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as Record<string, unknown>);
    const bKeys = Object.keys(b as Record<string, unknown>);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (
        (a as Record<string, unknown>)[key] !==
        (b as Record<string, unknown>)[key]
      ) {
        return false;
      }
    }
    return true;
  }
  return false;
}

// 필터 속성명을 API 파라미터명으로 매핑
const FILTER_PARAM_MAPPING: Record<
  string,
  string | ((value: any, params: URLSearchParams) => void)
> = {
  // 조직 관련 - store는 특별 처리 (storeCodeList로 전달)
  organization: 'organizationCodes',
  business: 'businessCodes',
  store: 'storeCd', // 특별 처리됨

  // 날짜 관련 - 특별 처리
  dateRange: (value: FilterState['dateRange'], params: URLSearchParams) => {
    if (value.start) params.append('startDate', value.start.replace(/-/g, ''));
    if (value.end) params.append('endDate', value.end.replace(/-/g, ''));
  },
  dateType: 'dateType',

  // 카드 매출 관련
  cardSalesOptions: 'cardSalesOptions',
  cardSalesDateType: 'cardSalesDateType',

  // OCT 관련
  octDateType: 'octDateType',
  octCStatus: 'octCStatus',
  octBOption: 'octBOption',

  // 페이지 옵션
  pageOptions: 'pageOptions',
  salesinfoPageOptions: 'pageOptions',

  // 검색 필드
  carNumber: 'car',
  cardNumber: 'cardNum',
  approvalNumber: 'approvalNo',
  issueCode: 'issueCode',

  // 승인 타입
  approvalType: 'approvalType',

  // 거래 관련
  dealType: 'dealType',
  dealer: 'dealer',
  cardDealer: 'cardDealer',

  // 주유 관련
  oil: 'oil',
  tank: 'tank',
  self: 'self',
  nozzle: 'nozzle',
  cat: 'cat',

  // 금액 범위 - 특별 처리
  amountRange: (value: FilterState['amountRange'], params: URLSearchParams) => {
    if (value.min) params.append('startAmount', value.min);
    if (value.max) params.append('endAmount', value.max);
  },

  // 마감 관련
  closeDate: 'closeDate',
  closeCount: 'closeCount',

  // 기타
  purchaseAdjustment: 'purchaseAdjustment',
  carWashRefund: 'carWashRefund',
  carWashIsIncludeRefund: 'isIncludeRefund',
  isExcludeTankLorry: 'isExcludeTankLorry',
};

// 페이지별 특수 파라미터 처리 함수
const applyPageSpecificParams = (
  params: URLSearchParams,
  pageId: string,
  filters: FilterState,
  customOptions: Record<string, any> = {}
): void => {
  // 페이지별 특수 처리
  switch (pageId) {
    case 'cardsales':
      // 카드 매출 페이지 전용 처리
      if (filters.pageOptions.includes('neTruck')) {
        params.append('isNeTruck', 'true');
      }
      if (filters.pageOptions.includes('inMoney')) {
        params.append('isInMoney', 'true');
      }
      if (filters.pageOptions.includes('cashBill')) {
        params.append('isCashBill', 'true');
      }
      break;

    case 'selfsales':
      // 셀프 판매 페이지 전용 처리
      if (filters.pageOptions.includes('rePay')) {
        params.append('isRePay', 'true');
      }
      if (filters.pageOptions.includes('bfcancel')) {
        params.append('isBfCancel', 'true');
      }
      if (filters.pageOptions.includes('afcancel')) {
        params.append('isAfCancel', 'true');
      }
      if (filters.pageOptions.includes('errProcess')) {
        params.append('isErrProcess', 'true');
      }
      break;

    // 다른 페이지들의 특수 처리도 여기 추가 가능
  }

  // 커스텀 옵션 처리
  Object.entries(customOptions).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });
};
